/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook to use sae columns.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type columns
 */


/*
 * Used to format datetimes
 */
import { DateTime } from 'luxon';



/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../api/dtos';
import { GridColDef } from '@mui/x-data-grid';
import { Box, styled } from '@mui/material';

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const WrappingBox = styled(Box)(() => ({
    whiteSpace: 'normal',
    wordWrap: 'break-word'
}))

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

const useSaeColumns = (institutions: Dtos.IInstitution[] | null | undefined, showSite: boolean, invalid?: boolean): Array<GridColDef<Dtos.Sae<Dtos.SaeForm, Dtos.Patient>>> => {
    const columns = React.useMemo(() => {
        const cols: Array<GridColDef<Dtos.Sae<Dtos.SaeForm, Dtos.Patient>> | boolean> = [
            {
                field: 'patient.studyNumber',
                headerName: 'Study Number',
                valueGetter: (_value, row) => row.patient.studyNumber,
                renderCell: (params) => (params.row.patient.studyNumber),
                width: 110
            },
            {
                field: 'saeNumber',
                headerName: 'SAE No',
                renderCell: (params) => (params.row.saeNumber),
                width: 80
            },
            invalid !== true && {
                field: 'form.dateOfSAE',
                headerName: 'Onset Date',
                valueGetter: (_value, row) => row.form.dateOfOnset,
                renderCell: (params) => (params.row.form.dateOfOnset ? DateTime.fromISO(params.row.form.dateOfOnset).toFormat('dd/MM/yyyy') : '' ),
                width: 110
            },
            invalid !== true && {
                field: 'form.eventName',
                headerName: 'Event Name',
                valueGetter: (_value, row) => {
                    return row.form.eventName
                },
                renderCell: (params) => (<WrappingBox>{params.row.form.eventName}</WrappingBox>),
                flex: 1
            },
            showSite && {
                field: 'institutionId',
                headerName: 'Site',
                valueGetter: (_value, row) => {
                    return institutions?.find(i => i.id === (row.patient.institutionId))?.name
                },
                renderCell: (params) => (<WrappingBox>{params.value}</WrappingBox>),
                flex: 1
            },
            invalid !== true && {
                field: 'form.eventStatus',
                headerName: 'Status',
                valueGetter: (_value, row) => 
                    (row.form.followUpNumber as number > 0 ? 'Follow-up no.' + row.form.followUpNumber : 'Initial') + ' ' +
                    (row.form.eventStatus ? '(' + (row.form.eventStatus.match(/Ongoing.*/) ? 'Ongoing' : row.form.eventStatus) + ')' : ''),
                renderCell: (params) => (
                    <WrappingBox>
                    {params.value}
                    </WrappingBox>
                )
            },
            invalid === true && {
                field: 'invalidForm.date',
                headerName: 'Date Marked Invalid',
                renderCell: (params) => (params.row.invalidForm?.date ? DateTime.fromISO(params.row.invalidForm.date).toFormat('dd/MM/yyyy') : '')
            }
        ];

        return cols.filter(column => !!column) as Array<GridColDef<Dtos.Sae<Dtos.SaeForm, Dtos.Patient>>>;
    }, [institutions, showSite, invalid]);

    return columns;
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default useSaeColumns;
